/**
 * AuditLog - View admin action history and system events
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  History, Search, Filter, User, Settings, Shield, CreditCard,
  Mail, UserCheck, FileText, Loader2, Calendar, Clock,
  ChevronLeft, ChevronRight, Download
} from 'lucide-react';
import api from '@/lib/api';

const ACTION_TYPES = [
  { id: 'all', name: 'All Actions', icon: History },
  { id: 'auth', name: 'Authentication', icon: Shield },
  { id: 'delegate', name: 'Delegate Management', icon: User },
  { id: 'checkin', name: 'Check-ins', icon: UserCheck },
  { id: 'payment', name: 'Payments', icon: CreditCard },
  { id: 'email', name: 'Communications', icon: Mail },
  { id: 'settings', name: 'Settings Changes', icon: Settings },
  { id: 'content', name: 'Content Updates', icon: FileText },
  { id: 'team', name: 'Team Changes', icon: User },
];

const ACTION_COLORS = {
  create: 'bg-green-100 text-green-800',
  update: 'bg-blue-100 text-blue-800',
  delete: 'bg-red-100 text-red-800',
  login: 'bg-purple-100 text-purple-800',
  checkin: 'bg-cyan-100 text-cyan-800',
  payment: 'bg-amber-100 text-amber-800',
  email: 'bg-pink-100 text-pink-800',
  error: 'bg-red-100 text-red-800',
  system: 'bg-slate-100 text-slate-800',
};

const AuditLog = ({ eventId }) => {
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 25;

  // Fetch audit logs
  const { data, isLoading } = useQuery({
    queryKey: ['audit-logs', eventId, filterType, page, dateFrom, dateTo],
    queryFn: () => api.get(`/events/${eventId}/audit-logs`, {
      params: {
        type: filterType, page, limit: pageSize,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      },
    }).then(r => r.data),
  });

  const logs = data?.logs || [];
  const totalPages = data?.total_pages || 1;
  const totalCount = data?.total_count || 0;

  // Filter logs by search query
  const filteredLogs = logs.filter(log => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      log.action?.toLowerCase().includes(query) ||
      log.actor_name?.toLowerCase().includes(query) ||
      log.details?.toLowerCase().includes(query) ||
      log.target?.toLowerCase().includes(query)
    );
  });

  const getActionColor = (action) => {
    const actionLower = action?.toLowerCase() || '';
    if (actionLower.includes('create') || actionLower.includes('add')) return ACTION_COLORS.create;
    if (actionLower.includes('update') || actionLower.includes('edit')) return ACTION_COLORS.update;
    if (actionLower.includes('delete') || actionLower.includes('remove')) return ACTION_COLORS.delete;
    if (actionLower.includes('login') || actionLower.includes('auth')) return ACTION_COLORS.login;
    if (actionLower.includes('checkin') || actionLower.includes('check-in')) return ACTION_COLORS.checkin;
    if (actionLower.includes('payment') || actionLower.includes('charge')) return ACTION_COLORS.payment;
    if (actionLower.includes('email') || actionLower.includes('send')) return ACTION_COLORS.email;
    if (actionLower.includes('error') || actionLower.includes('fail')) return ACTION_COLORS.error;
    return ACTION_COLORS.system;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const exportLogs = async (format = 'csv') => {
    try {
      const response = await api.get(`/events/${eventId}/audit-logs/export`, {
        params: {
          format,
          type: filterType !== 'all' ? filterType : undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
        },
        responseType: format === 'csv' ? 'blob' : 'json',
      });
      const blob = format === 'csv'
        ? response.data
        : new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_log_${eventId}_${new Date().toISOString().split('T')[0]}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  return (
    <div className="space-y-6" data-testid="audit-log">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <History className="h-6 w-6 text-slate-600" />
            Audit Log
          </h2>
          <p className="text-slate-500">Track all admin actions and system events</p>
        </div>
        <Button variant="outline" onClick={() => exportLogs('csv')} data-testid="audit-export-csv">
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search actions, users, or details..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              {/* Type Filter */}
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-full max-w-[200px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  {ACTION_TYPES.map(type => {
                    const Icon = type.icon;
                    return (
                      <SelectItem key={type.id} value={type.id}>
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4" />
                          {type.name}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            
            {/* Date Range Filters */}
            <div className="flex flex-col sm:flex-row gap-4 items-center">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-slate-400" />
                <span className="text-sm text-slate-500">From:</span>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                  className="w-full max-w-[160px]"
                  data-testid="audit-date-from"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">To:</span>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                  className="w-full max-w-[160px]"
                  data-testid="audit-date-to"
                />
              </div>
              {(dateFrom || dateTo) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setDateFrom(''); setDateTo(''); setPage(1); }}
                  className="text-slate-500"
                >
                  Clear dates
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Log Entries */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Activity Log</CardTitle>
            <Badge variant="secondary">{totalCount} entries</Badge>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : filteredLogs.length > 0 ? (
            <ScrollArea className="h-[500px]">
              <div className="space-y-2">
                {filteredLogs.map((log, index) => (
                  <div
                    key={log.id || index}
                    className="flex items-start gap-4 p-3 rounded-lg border hover:bg-slate-50 transition-colors"
                  >
                    {/* Timestamp */}
                    <div className="flex-shrink-0 text-xs text-slate-500 w-32">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(log.timestamp).toLocaleDateString()}
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        <Clock className="h-3 w-3" />
                        {new Date(log.timestamp).toLocaleTimeString()}
                      </div>
                    </div>

                    {/* Action Badge */}
                    <Badge className={`${getActionColor(log.action)} flex-shrink-0`}>
                      {log.action}
                    </Badge>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{log.description || log.action}</p>
                      {log.target && (
                        <p className="text-xs text-slate-500 mt-1">
                          Target: {log.target}
                        </p>
                      )}
                      {log.details && typeof log.details === 'string' && (
                        <p className="text-xs text-slate-400 mt-1 truncate">
                          {log.details}
                        </p>
                      )}
                      {log.details && typeof log.details === 'object' && Object.keys(log.details).length > 0 && (
                        <p className="text-xs text-slate-400 mt-1 truncate">
                          {JSON.stringify(log.details)}
                        </p>
                      )}
                    </div>

                    {/* Actor */}
                    <div className="flex-shrink-0 text-right">
                      <div className="flex items-center gap-1 text-sm">
                        <User className="h-3 w-3" />
                        {log.actor_name || 'System'}
                      </div>
                      {log.actor_email && (
                        <p className="text-xs text-slate-400">{log.actor_email}</p>
                      )}
                      {log.ip_address && (
                        <p className="text-xs text-slate-400">{log.ip_address}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          ) : (
            <div className="text-center py-12">
              <History className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No audit logs found</p>
              <p className="text-sm text-slate-400 mt-1">
                Actions will be recorded as they happen
              </p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-slate-500">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLog;
